import React from "react";
import classNames from "classnames";
import {
    getJournalEntryDisplaySource,
    getShortJournalEntryReferenceNumber,
} from "@common/helpers/accounting";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import { JournalEntrySource } from "@common/types/domains/accounting/journalEntry";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { journalEntrySourceFilterToLabelMap } from "@components/accounting/constants";
import { useIdCopy } from "@hooks/useIdCopy";
import { useReportAmountDisplay } from "../../useReportAmountDisplay";
import styles from "../GeneralLedgerTable.module.scss";
import { CLICKABLE_SOURCE_TYPES, GL_COPY_LABEL } from "./glCellHelpers";

interface JournalLineSourceCellProps {
    source: JournalEntrySource;
    isRecurring: boolean;
    onClick?: () => void;
}

export const JournalLineSourceCell = ({
    source,
    isRecurring,
    onClick,
}: JournalLineSourceCellProps): React.ReactElement => {
    const isClickable = CLICKABLE_SOURCE_TYPES.has(source.type) && !!onClick;
    const label = isRecurring
        ? journalEntrySourceFilterToLabelMap[JournalEntrySourceFilter.Recurring]
        : getJournalEntryDisplaySource(source);

    return (
        <ViewTableCell
            captureClicks={isClickable}
            className={classNames({ [styles.clickableCell]: isClickable })}
            onClick={isClickable ? onClick : undefined}
        >
            {label}
        </ViewTableCell>
    );
};

interface JournalLineReferenceCellProps {
    referenceNumber: string;
    onClick?: () => void;
}

export const JournalLineReferenceCell = ({
    referenceNumber,
    onClick,
}: JournalLineReferenceCellProps): React.ReactElement => {
    const { getCopyProps } = useIdCopy({ label: GL_COPY_LABEL });
    const isClickable = !!onClick;

    return (
        <ViewTableCell
            captureClicks={isClickable}
            className={classNames({ [styles.clickableCell]: isClickable })}
            onClick={onClick}
            {...getCopyProps(referenceNumber)}
        >
            {getShortJournalEntryReferenceNumber(referenceNumber)}
        </ViewTableCell>
    );
};

export type JournalLineBalanceKind =
    | "group"
    | "openingBalance"
    | "closingBalance"
    | "transaction";

interface JournalLineBalanceCellProps {
    kind: JournalLineBalanceKind;
    amount: number;
}

export const JournalLineBalanceCell = ({
    kind,
    amount,
}: JournalLineBalanceCellProps): React.ReactElement => {
    const { text, negativeClassName } = useReportAmountDisplay(amount);

    return (
        <ViewTableCell
            className={classNames(styles.numericCell, negativeClassName, {
                [styles.accountCell]: kind === "group",
                [styles.endingBalanceLabelCell]: kind === "closingBalance",
            })}
        >
            {text}
        </ViewTableCell>
    );
};
